import type { DeviceVersionUpdate } from '@dyspatch/core';
import { AlertTriangle, ArrowUp } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { CommandBox } from '@/features/fleet/ConnectDevice';
import { cn } from '@/lib/utils';

/**
 * A device that is not on the build its fleet is on, drawn once for the three
 * places that show devices.
 *
 * The first cut of this was a warning badge and an orange sentence on every
 * stale device, which got the weight exactly wrong. Being behind is the normal
 * state of a laptop between a release and its next idle minute: the daemon
 * installs the new build itself, nobody is interrupted, and there is nothing
 * for the person reading the page to do. Painting that in alarm colours is how
 * a colour stops meaning anything, and the one time it does matter - a device
 * below the fleet's floor, taking no work at all - it would have looked
 * identical.
 *
 * So the two states are drawn as what they are. `behind` is a quiet line in the
 * card, with the command folded away behind a link for the person who would
 * rather not wait. `blocked` is a red badge on the row and the same line opened,
 * because that machine has stopped and somebody has to act.
 *
 * The sentence itself comes from the hub (`DeviceVersionUpdate.detail`), so the
 * fleet floor, the two version numbers and what happens next are written in one
 * place rather than reassembled differently on each surface.
 */

/**
 * The compact form, for a row with no space for a sentence: the fleet table,
 * where an admin is scanning thirty machines rather than reading one.
 *
 * It names the version rather than saying "out of date", because the useful
 * thing when several devices are stale is which builds are out there. A bare
 * "0.1.0" in a chip read as a code nobody could place, so the chip says what
 * the number is. The sentence rides along as the title.
 */
export function VersionBadge({ update }: { update: DeviceVersionUpdate }) {
  return update.status === 'blocked' ? (
    <Badge variant="destructive" title={update.detail}>
      Needs {update.to}
    </Badge>
  ) : (
    <Badge variant="outline" className="text-fg-3" title={update.detail}>
      on <span className="font-mono">{update.from}</span>
    </Badge>
  );
}

/**
 * The long form, for a card about one machine.
 *
 * `blocked` opens with the command already showing: the device is idle in the
 * worst way and the next thing anybody does is run that. `behind` keeps it
 * folded, because the honest instruction is "nothing, it handles itself", and
 * an unfoldable command box would imply otherwise.
 */
export function DeviceUpdateNotice({
  update,
  name,
  className,
}: {
  update: DeviceVersionUpdate;
  /** The device's own name, so the sentence is about a machine rather than about "it". */
  name: string;
  className?: string;
}) {
  const blocked = update.status === 'blocked';
  const [open, setOpen] = useState(false);
  const showCommand = blocked || open;

  return (
    <div
      className={cn(
        'rounded-md border border-dashed px-2.5 py-2',
        blocked ? 'border-bad/40' : 'border-line-2',
        className,
      )}
    >
      <div className="flex min-w-0 items-start gap-2">
        {blocked ? (
          <AlertTriangle className="text-bad mt-px size-3.5 shrink-0" aria-hidden />
        ) : (
          <ArrowUp className="text-fg-3 mt-px size-3.5 shrink-0" aria-hidden />
        )}
        <p className={cn('min-w-0 flex-1 text-xs', blocked ? 'text-bad' : 'text-fg-2')}>
          {name} {update.detail}.
        </p>
        {blocked ? null : (
          <button
            type="button"
            className="text-fg-3 hover:text-foreground shrink-0 text-2xs underline underline-offset-2"
            onClick={() => setOpen((current) => !current)}
          >
            {open ? 'Hide' : 'Do it now'}
          </button>
        )}
      </div>
      {showCommand ? (
        <div className="mt-1.5">
          <CommandBox command="dyspatch-worker upgrade" />
        </div>
      ) : null}
    </div>
  );
}
